export interface WaveformProcessorOptions {
  barCount?: number;
  smoothingFactor?: number;
  amplitudeScale?: number;
  minVisibleHeight?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

interface ResolvedWaveformProcessorOptions {
  barCount: number;
  smoothingFactor: number;
  amplitudeScale: number;
  minVisibleHeight: number;
}

const DEFAULT_OPTIONS: ResolvedWaveformProcessorOptions = {
  barCount: 3,
  smoothingFactor: 0.6,
  amplitudeScale: 3,
  minVisibleHeight: 0.05,
};

const resolveOptions = (options?: WaveformProcessorOptions): ResolvedWaveformProcessorOptions => ({
  barCount: clamp(Math.floor(options?.barCount ?? DEFAULT_OPTIONS.barCount), 1, 48),
  smoothingFactor: clamp(options?.smoothingFactor ?? DEFAULT_OPTIONS.smoothingFactor, 0, 1),
  amplitudeScale: Math.max(options?.amplitudeScale ?? DEFAULT_OPTIONS.amplitudeScale, 0),
  minVisibleHeight: clamp(options?.minVisibleHeight ?? DEFAULT_OPTIONS.minVisibleHeight, 0, 1),
});

export class WaveformProcessor {
  private readonly options: ResolvedWaveformProcessorOptions;
  private previousHeights?: number[];

  constructor(options?: WaveformProcessorOptions) {
    this.options = resolveOptions(options);
  }

  reset(): void {
    this.previousHeights = undefined;
  }

  process(samples: Float32Array): number[] | undefined {
    const { barCount, amplitudeScale, smoothingFactor, minVisibleHeight } = this.options;

    if (samples.length === 0) {
      this.reset();
      return undefined;
    }

    const samplesPerBar = Math.max(Math.floor(samples.length / barCount), 1);
    const barHeights: number[] = [];

    for (let barIndex = 0; barIndex < barCount; barIndex++) {
      const start = barIndex * samplesPerBar;
      const end =
        barIndex === barCount - 1
          ? samples.length
          : Math.min(start + samplesPerBar, samples.length);

      const segmentLength = end - start;
      if (segmentLength <= 0) {
        barHeights.push(0);
        continue;
      }

      let sumSquares = 0;
      for (let sampleIndex = start; sampleIndex < end; sampleIndex++) {
        const sample = samples[sampleIndex];
        sumSquares += sample * sample;
      }

      const rms = Math.sqrt(sumSquares / segmentLength);
      barHeights.push(clamp(rms * amplitudeScale, 0, 1));
    }

    const smoothedHeights = barHeights.map((height, index) => {
      const previousHeight = this.previousHeights?.[index];
      if (previousHeight === undefined) return height;
      return previousHeight * smoothingFactor + height * (1 - smoothingFactor);
    });

    this.previousHeights = smoothedHeights;

    const shouldDisplay = smoothedHeights.some(height => height >= minVisibleHeight);
    return shouldDisplay ? smoothedHeights : undefined;
  }
}

export const createWaveformProcessor = (options?: WaveformProcessorOptions): WaveformProcessor =>
  new WaveformProcessor(options);

export const computeRms = (samples: Float32Array): number => {
  if (samples.length === 0) return 0;

  let sumSquares = 0;
  for (let index = 0; index < samples.length; index++) {
    const sample = samples[index];
    sumSquares += sample * sample;
  }

  return Math.sqrt(sumSquares / samples.length);
};

export const bytesToFloat32 = (bytes: Uint8Array): Float32Array => {
  const samples = new Float32Array(bytes.length);
  for (let index = 0; index < bytes.length; index++) {
    samples[index] = (bytes[index] - 128) / 128;
  }
  return samples;
};
